import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, FormControl,
  InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, Card, CardContent,
  CircularProgress, Alert, Divider,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Print as PrintIcon,
  Info as InfoIcon, ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import OrderMedicationService from '../services/OrderMedicationService';
import { useResidents } from '../hooks/useResidents';
import { MedicationOrderDTO, MedicationRoute } from '../types/medication.types';

const medicationItemSchema = yup.object({
  medicationName: yup.string().required('Medication name is required'),
  dosage: yup.string().required('Dosage is required'),
  route: yup.mixed<MedicationRoute>().oneOf(Object.values(MedicationRoute)).required('Route is required'),
  frequency: yup.string().required('Frequency is required'),
  startDate: yup.date().required('Start date is required').nullable(),
  endDate: yup.date().nullable(),
});

const orderSchema = yup.object({
  residentId: yup.string().required('Resident is required'),
  prescribingPhysician: yup.string().required('Prescribing physician is required'),
  medicationItems: yup.array().of(medicationItemSchema).min(1, 'At least one medication is required'),
});

type OrderFormData = yup.InferType<typeof orderSchema>;

const OrderMedicationPage: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [medications, setMedications] = useState<MedicationOrderDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    residents,
    loading: residentsLoading,
    fetchResidents,
  } = useResidents();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OrderFormData>({
    resolver: yupResolver(orderSchema),
    defaultValues: {
      medicationItems: [{ startDate: new Date() }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "medicationItems",
  });

  // Fetch medications from service
  const fetchMedications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await OrderMedicationService.getAllOrders(0, 100);
      setMedications(response.content || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch medication orders');
      console.error('Error fetching medications:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    fetchMedications();
    fetchResidents(0, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenDialog = () => {
    reset({
      medicationItems: [{ startDate: new Date() }]
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset();
  };

  const onSubmitOrder = async (data: OrderFormData) => {
    try {
      setLoading(true);
      await OrderMedicationService.createOrder({
        residentId: data.residentId,
        prescribingPhysicianName: data.prescribingPhysician,
        medicationItems: data.medicationItems?.map(item => ({
          medicationName: item.medicationName,
          dosage: item.dosage,
          route: item.route as MedicationRoute,
          frequency: item.frequency,
          startDate: item.startDate?.toISOString().split('T')[0] || '',
          endDate: item.endDate?.toISOString().split('T')[0],
          specialInstructions: `${item.frequency} via ${item.route}`,
        })) || [],
      });
      handleCloseDialog();
      await fetchMedications(); // Refresh list
    } catch (err: any) {
      setError(err.message || 'Failed to create medication order');
      console.error('Failed to create medication order:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this medication order?')) {
      try {
        setLoading(true);
        await OrderMedicationService.deleteOrder(id);
        await fetchMedications();
      } catch (err: any) {
        setError(err.message || 'Failed to delete medication order');
        console.error('Failed to delete medication order:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      setLoading(true);
      await OrderMedicationService.updateOrderStatus(id, newStatus as any);
      await fetchMedications();
    } catch (err: any) {
      setError(err.message || 'Failed to update medication status');
      console.error('Failed to update medication status:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredMedications = medications.filter((med) => {
    const resident = residents.find((r) => r.id === med.residentId);
    const residentName = resident ? `${resident.firstName} ${resident.lastName}` : '';
    const medicationNames = med.medicationItems?.map(item => item.medicationName).join(' ') || '';

    return residentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      medicationNames.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const viewingMed = viewingId ? medications.find((m) => m.id === viewingId) : null;
  const viewingResident = viewingMed ? residents.find((r) => r.id === viewingMed.residentId) : null;

  // Calculate stats
  const activeCount = medications.filter((m) => m.status === 'ACTIVE').length;
  const discontinuedCount = medications.filter((m) => m.status === 'CANCELLED').length;

  if (loading && medications.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2CA6A4' }}>
          Medication Orders
        </Typography>
        <Button
          variant="contained"
          startIcon={<ShoppingCartIcon />}
          onClick={handleOpenDialog}
          sx={{
            background: 'linear-gradient(135deg, #2CA6A4 0%, #174A5A 100%)',
          }}
        >
          New Order
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #2CA6A4 0%, #174A5A 100%)', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {medications.length}
              </Typography>
              <Typography variant="body2">Total Orders</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {activeCount}
              </Typography>
              <Typography variant="body2">Active Orders</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {discontinuedCount}
              </Typography>
              <Typography variant="body2">Cancelled</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by resident name or medication..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Paper>

      {/* Orders Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Resident Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Medications</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Physician</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredMedications.length > 0 ? (
              filteredMedications.map((med) => {
                const resident = residents.find((r) => r.id === med.residentId);
                return (
                  <TableRow key={med.id}>
                    <TableCell>
                      {resident ? `${resident.firstName} ${resident.lastName}` : med.residentId}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {med.medicationItems?.map(item => item.medicationName).join(', ')}
                      {med.medicationItems && med.medicationItems.length > 3 && '...'}
                    </TableCell>
                    <TableCell>{med.prescribingPhysicianName || 'N/A'}</TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={med.status}
                          onChange={(e) => med.id && handleStatusChange(med.id, e.target.value)}
                        >
                          <MenuItem value="ACTIVE">Active</MenuItem>
                          <MenuItem value="ON_HOLD">On Hold</MenuItem>
                          <MenuItem value="CANCELLED">Cancelled</MenuItem>
                          <MenuItem value="COMPLETED">Completed</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setViewingId(med.id || null)}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Print">
                        <IconButton size="small" color="primary">
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => med.id && handleDelete(med.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    No medication orders found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Order Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Create New Medication Order
        </DialogTitle>
        <DialogContent dividers>
          <form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="residentId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.residentId}>
                      <InputLabel>Select Resident</InputLabel>
                      <Select {...field} label="Select Resident">
                        {residentsLoading ? (
                          <MenuItem disabled>Loading residents...</MenuItem>
                        ) : residents.length === 0 ? (
                          <MenuItem disabled>No residents found</MenuItem>
                        ) : (
                          residents.map((resident) => (
                            <MenuItem key={resident.id} value={resident.id}>
                              {resident.firstName} {resident.lastName} - Room {resident.roomNumber || 'N/A'}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      {errors.residentId && (
                        <Typography color="error" variant="caption">
                          {errors.residentId.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="prescribingPhysician"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Prescribing Physician"
                      fullWidth
                      error={!!errors.prescribingPhysician}
                      helperText={errors.prescribingPhysician?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>Medications</Divider>
              </Grid>

              {fields.map((item, index) => (
                <React.Fragment key={item.id}>
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Medication #{index + 1}
                    </Typography>
                    {fields.length > 1 && (
                      <IconButton onClick={() => remove(index)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`medicationItems.${index}.medicationName`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Medication Name"
                          fullWidth
                          error={!!errors.medicationItems?.[index]?.medicationName}
                          helperText={errors.medicationItems?.[index]?.medicationName?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`medicationItems.${index}.dosage`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Dosage"
                          fullWidth
                          placeholder="e.g., 10mg, 500mg"
                          error={!!errors.medicationItems?.[index]?.dosage}
                          helperText={errors.medicationItems?.[index]?.dosage?.message}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`medicationItems.${index}.route`}
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.medicationItems?.[index]?.route}>
                          <InputLabel>Route</InputLabel>
                          <Select {...field} label="Route">
                            {Object.values(MedicationRoute).map((route) => (
                              <MenuItem key={route} value={route}>
                                {route}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.medicationItems?.[index]?.route && (
                            <Typography color="error" variant="caption">
                              {errors.medicationItems?.[index]?.route?.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`medicationItems.${index}.frequency`}
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.medicationItems?.[index]?.frequency}>
                          <InputLabel>Frequency</InputLabel>
                          <Select {...field} label="Frequency">
                            <MenuItem value="Once daily">Once daily</MenuItem>
                            <MenuItem value="Twice daily">Twice daily</MenuItem>
                            <MenuItem value="Three times daily">Three times daily</MenuItem>
                            <MenuItem value="Four times daily">Four times daily</MenuItem>
                            <MenuItem value="Every 4 hours">Every 4 hours</MenuItem>
                            <MenuItem value="Every 6 hours">Every 6 hours</MenuItem>
                            <MenuItem value="Every 8 hours">Every 8 hours</MenuItem>
                            <MenuItem value="As needed">As needed (PRN)</MenuItem>
                          </Select>
                          {errors.medicationItems?.[index]?.frequency && (
                            <Typography color="error" variant="caption">
                              {errors.medicationItems?.[index]?.frequency?.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Controller
                        name={`medicationItems.${index}.startDate`}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <DatePicker
                            label="Start Date"
                            value={value}
                            onChange={onChange}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.medicationItems?.[index]?.startDate,
                                helperText: errors.medicationItems?.[index]?.startDate?.message,
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Controller
                        name={`medicationItems.${index}.endDate`}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <DatePicker
                            label="End Date (Optional)"
                            value={value}
                            onChange={onChange}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                              },
                            }}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                </React.Fragment>
              ))}

              <Grid item xs={12}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => append({
                    medicationName: '',
                    dosage: '',
                    route: MedicationRoute.ORAL,
                    frequency: '',
                    startDate: new Date()
                  })}
                  variant="outlined"
                  fullWidth
                >
                  Add Another Medication
                </Button>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit(onSubmitOrder)}
            variant="contained"
            sx={{ background: 'linear-gradient(135deg, #2CA6A4 0%, #174A5A 100%)' }}
          >
            Submit Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Order Details Dialog */}
      <Dialog open={!!viewingMed} onClose={() => setViewingId(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Medication Order Details
        </DialogTitle>
        <DialogContent dividers>
          {viewingMed && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Resident Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {viewingResident ? `${viewingResident.firstName} ${viewingResident.lastName}` : viewingMed.residentId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Prescribing Physician
                    </Typography>
                    <Typography variant="body1">{viewingMed.prescribingPhysicianName}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Status
                    </Typography>
                    <Chip
                      label={viewingMed.status}
                      color={viewingMed.status === 'ACTIVE' ? 'success' : 'default'}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Medications</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Dosage</TableCell>
                            <TableCell>Route</TableCell>
                            <TableCell>Frequency</TableCell>
                            <TableCell>Start Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {viewingMed.medicationItems?.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell sx={{ fontWeight: 'bold' }}>{item.medicationName}</TableCell>
                              <TableCell>{item.dosage}</TableCell>
                              <TableCell>{item.route}</TableCell>
                              <TableCell>{item.frequency}</TableCell>
                              <TableCell>{item.startDate}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setViewingId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderMedicationPage;
